// Field copy API (backend: api/routes/field_copy.py, owner-only).
//
// - GET  /api/field-copy/converter?source&target → ConverterInfo (404: field not in the Jira cache)
// - POST /api/field-copy/count {jql}             → {count, max}
// - POST /api/field-copy/runs {jql, pairs}       → 202 {run_id} | 400 validation | 409 busy
// - GET  /api/field-copy/runs, /runs/{id}        → summaries / detail with live row statuses
// - POST /runs/{id}/apply|revert {confirm}       → preview (confirm=false) | 202
// - POST /runs/{id}/stop|resume|retry
import { ApiError } from '@/lib/http'

export { ApiError }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  })
  if (!res.ok) {
    let detail = await res.text()
    try {
      const parsed: unknown = JSON.parse(detail)
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        typeof (parsed as { detail?: unknown }).detail === 'string'
      ) {
        detail = (parsed as { detail: string }).detail
      }
    } catch {
      // keep raw text
    }
    throw new ApiError(res.status, detail || `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export type FieldCopyMode = 'only_empty' | 'overwrite' | 'append'
export const MODES: FieldCopyMode[] = ['only_empty', 'overwrite', 'append']
export const MODE_LABEL: Record<FieldCopyMode, string> = {
  only_empty: 'fill only empty',
  overwrite: 'overwrite',
  append: 'append (multi-value)',
}

export type FieldRef = { id: string; name: string; kind: string }
export type ConverterInfo = {
  source: FieldRef
  target: FieldRef
  converter: string | null
  label: string | null
  reason: string | null
  append_allowed: boolean
}

export type PairInput = { source: string; target: string; mode: FieldCopyMode }
export type PairDraft = PairInput & {
  key: number
  info: ConverterInfo | null
  error: string | null
  checking: boolean
}

let nextPairKey = 0
export function newPairDraft(): PairDraft {
  nextPairKey += 1
  return { key: nextPairKey, source: '', target: '', mode: 'only_empty', info: null, error: null, checking: false }
}

export type RunStatus =
  | 'planning' | 'planned' | 'applying' | 'applied' | 'paused' | 'cancelled' | 'failed'
  | 'reverting' | 'reverted'
export const ACTIVE_STATUSES: readonly RunStatus[] = ['planning', 'applying', 'reverting']
export const isActive = (status: RunStatus): boolean => ACTIVE_STATUSES.includes(status)

export type Outcome = 'write' | 'equal' | 'target_filled' | 'source_empty' | 'unconvertible'
export const OUTCOMES: Outcome[] = ['write', 'equal', 'target_filled', 'source_empty', 'unconvertible']

export type Example = { issue_key: string; source: string; target: string; new: string | null; reason: string | null }
export type PairCounters = {
  outcomes: Partial<Record<Outcome, number>>
  examples?: Partial<Record<Outcome, Example[]>>
  bulk?: number
  single?: number
}
export type AutomationRule = {
  target: string
  rule_uuid: string | null
  name: string | null
  state: string | null
  where: string[]
}
export type RunCounters = {
  pairs?: PairCounters[]
  estimate?: { bulk: number; single: number; bulk_groups: number }
  automation_rules?: AutomationRule[]
  warnings?: string[]
  last_phase?: 'apply' | 'revert' | null
}
export type RunPair = PairInput & {
  source_kind: string
  target_kind: string
  converter: string
  source_name: string
  target_name: string
}
export type RunSummary = {
  id: number
  actor: string
  jql: string
  pairs: RunPair[]
  status: RunStatus
  counters: RunCounters
  issues_total: number
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  heartbeat_at: string | null
  error: string | null
}
export type RunDetail = RunSummary & {
  by_status: Record<string, number>
  by_pair_status: Record<string, number>[]
}
export type ApplyPreview = {
  run_id: number
  rows: number
  issues: number
  bulk_rows: number
  single_rows: number
  automation_rules: AutomationRule[]
}
export type RevertPreview = { run_id: number; rows: number; issues: number }
export type RunAck = { run_id: number; status?: RunStatus; retried?: number }

/** Rows the plan would write, summed over all pairs. */
export const plannedWrites = (counters: RunCounters | null | undefined): number =>
  (counters?.pairs ?? []).reduce((sum, p) => sum + (p.outcomes.write ?? 0), 0)

export const getConverter = (source: string, target: string) =>
  request<ConverterInfo>(
    `/field-copy/converter?source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}`,
  )
export const countIssues = (jql: string) =>
  request<{ count: number; max: number }>('/field-copy/count', post({ jql }))
export const createRun = (jql: string, pairs: PairInput[]) =>
  request<{ run_id: number }>('/field-copy/runs', post({ jql, pairs }))
export const getRuns = (limit = 20) => request<RunSummary[]>(`/field-copy/runs?limit=${limit}`)
export const getRun = (id: number) => request<RunDetail>(`/field-copy/runs/${id}`)
export const previewApply = (id: number) =>
  request<ApplyPreview>(`/field-copy/runs/${id}/apply`, post({ confirm: false }))
export const confirmApply = (id: number) =>
  request<RunAck>(`/field-copy/runs/${id}/apply`, post({ confirm: true }))
export const previewRevert = (id: number) =>
  request<RevertPreview>(`/field-copy/runs/${id}/revert`, post({ confirm: false }))
export const confirmRevert = (id: number) =>
  request<RunAck>(`/field-copy/runs/${id}/revert`, post({ confirm: true }))
export const stopRun = (id: number) => request<RunAck>(`/field-copy/runs/${id}/stop`, post({}))
export const resumeRun = (id: number) => request<RunAck>(`/field-copy/runs/${id}/resume`, post({}))
export const retryRun = (id: number) => request<RunAck>(`/field-copy/runs/${id}/retry`, post({}))
